/**
 * Invoice Details Page
 * Main page component for viewing invoice details
 * US-INV-002: View Invoice Details
 */

import { useParams, useNavigate } from 'react-router-dom';
import { useInvoiceDetails } from '@/hooks/queries/useInvoiceDetails';
import { useRecordPayment } from '@/hooks/queries/useRecordPayment';
import { useSendInvoiceEmail } from '@/hooks/queries/useSendInvoiceEmail';
import { InvoiceHeader } from '@/components/invoices/InvoiceHeader';
import { LineItemsTable } from '@/components/invoices/LineItemsTable';
import { FinancialSummary } from '@/components/invoices/FinancialSummary';
import { PaymentHistory } from '@/components/invoices/PaymentHistory';
import { InvoiceActions } from '@/components/invoices/InvoiceActions';
import { RecordPaymentModal } from '@/components/invoices/modals/RecordPaymentModal';
import { SendEmailModal } from '@/components/invoices/modals/SendEmailModal';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { RecordPaymentRequest, SendInvoiceEmailRequest } from '@/types';
import { useState } from 'react';
import { AlertCircle, ArrowLeft, CheckCircle } from 'lucide-react';
import { apiClient } from '@/api/client';
import { toast } from '@/lib/toast';

export const InvoiceDetailsPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const invoiceId = parseInt(id || '0', 10);

  // Fetch invoice details
  const {
    data: invoice,
    isLoading,
    isError,
    error,
    refetch,
  } = useInvoiceDetails({ invoiceId });

  // Mutations
  const recordPaymentMutation = useRecordPayment({
    onSuccess: (data) => {
      toast.success(data.message || 'Payment has been successfully recorded.');
      setIsPaymentModalOpen(false);
      refetch();
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to record payment. Please try again.');
    },
  });

  const sendEmailMutation = useSendInvoiceEmail({
    onSuccess: (data) => {
      toast.success(data.message || 'Invoice has been sent successfully.');
      setIsEmailModalOpen(false);
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to send email. Please try again.');
    },
  });

  // Modal states
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  // Handlers
  const handleRecordPayment = (payment: RecordPaymentRequest) => {
    recordPaymentMutation.mutate({ invoiceId, payment });
  };

  const handleSendEmail = (emailRequest: SendInvoiceEmailRequest) => {
    sendEmailMutation.mutate({ invoiceId, emailRequest });
  };

  const handleDownloadPdf = async () => {
    try {
      setIsDownloading(true);
      const blob = await apiClient.downloadBlob(
        `/commerce-service/api/v1/invoices/${invoiceId}/download`
      );

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Invoice-${invoice?.invoiceNumber || invoiceId}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast.success('Invoice PDF download has started.');
    } catch (error: any) {
      toast.error(error.message || 'Failed to download PDF. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };

  const handleViewOrder = () => {
    if (invoice?.order?.orderId) {
      navigate(`/orders/${invoice.order.orderId}`);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="container mx-auto p-6 space-y-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-64 w-full" />
        <Skeleton className="h-96 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  // Error state
  if (isError || !invoice) {
    return (
      <div className="container mx-auto p-6">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error Loading Invoice</AlertTitle>
          <AlertDescription>
            {error?.message || 'Failed to load invoice details. Please try again.'}
          </AlertDescription>
        </Alert>
        <div className="mt-4 flex gap-2">
          <Button onClick={() => navigate(-1)} variant="outline">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Go Back
          </Button>
          <Button onClick={() => refetch()}>Retry</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Back Button */}
      <div className="flex items-center gap-2">
        <Button
          onClick={() => navigate(-1)}
          variant="ghost"
          size="sm"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back
        </Button>
      </div>

      {/* Success Alert for PAID invoices */}
      {invoice.status === 'PAID' && (
        <Alert>
          <CheckCircle className="h-4 w-4" />
          <AlertTitle>Invoice Paid</AlertTitle>
          <AlertDescription>
            This invoice has been paid in full.
          </AlertDescription>
        </Alert>
      )}

      {/* Invoice Header - AC-1 */}
      <InvoiceHeader invoice={invoice} />

      {/* Action Buttons - AC-4 */}
      <InvoiceActions
        invoiceId={invoice.invoiceId}
        invoiceStatus={invoice.status}
        orderId={invoice.order?.orderId}
        onDownloadPdf={handleDownloadPdf}
        onSendEmail={() => setIsEmailModalOpen(true)}
        onRecordPayment={() => setIsPaymentModalOpen(true)}
        onViewOrder={handleViewOrder}
        isDownloading={isDownloading}
        isSendingEmail={sendEmailMutation.isPending}
      />

      {/* Line Items Table - AC-2 */}
      <LineItemsTable lineItems={invoice.lineItems} />

      {/* Financial Summary - AC-2 */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          {/* Payment History - AC-3 */}
          <PaymentHistory payments={invoice.payments} />
        </div>
        <div>
          <FinancialSummary financialSummary={invoice.financialSummary} />
        </div>
      </div>

      {/* Modals */}
      <RecordPaymentModal
        open={isPaymentModalOpen}
        onClose={() => setIsPaymentModalOpen(false)}
        onSubmit={handleRecordPayment}
        isSubmitting={recordPaymentMutation.isPending}
        error={recordPaymentMutation.error?.message || null}
        maxAmount={invoice.financialSummary.balanceDue}
        invoiceNumber={invoice.invoiceNumber}
      />

      <SendEmailModal
        open={isEmailModalOpen}
        onClose={() => setIsEmailModalOpen(false)}
        onSubmit={handleSendEmail}
        isSubmitting={sendEmailMutation.isPending}
        error={sendEmailMutation.error?.message || null}
        customerEmail={invoice.customer.email}
        invoiceNumber={invoice.invoiceNumber}
      />
    </div>
  );
};
